import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  AvatarBadge,
  Box,
  Button,
  Center,
  Circle,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { routes } from "~/constants";
import { trackHeader as track } from "~/events";
import { useCohortCurrentUserParticipant } from "~/hooks/useCohortCurrentUserParticipant";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import { useSignOut } from "~/hooks/useSignOut";
import { HeartHand, Logo, Settings02, Stars02 } from "~/icons";
import avatar0 from "~/images/avatars/avatar0.svg";
import avatar1 from "~/images/avatars/avatar1.svg";
import avatar2 from "~/images/avatars/avatar2.svg";
import avatar3 from "~/images/avatars/avatar3.svg";
import avatar4 from "~/images/avatars/avatar4.svg";
import { fontSizes } from "~/utils/theme";
import { DesktopMenuItem } from "./DesktopMenuItem";

type Images = [string, string, string, string, string];
const images: Images = [avatar0, avatar1, avatar2, avatar3, avatar4];

export const AvatarMenu: FC = () => {
  const user = useCurrentUser();
  const participant = useCohortCurrentUserParticipant();
  const { handleSignOut } = useSignOut();
  const { t } = useTranslation();
  const upgradeRoute = routes.planSelect;

  const {
    avatarNumber,
    firstName,
    isOnboarded,
    lastName,
    subscription: { plan },
  } = user!;

  const isMissingInfo = !firstName && !lastName;
  const isUpgradeable = plan === "plus";
  const showBadge = isOnboarded && isMissingInfo;

  const imgSrc = participant?.profilePicture || images[avatarNumber];

  return (
    <Menu>
      <MenuButton
        as={Button}
        className="header-avatar-menu-button"
        bgColor="white"
        boxShadow="none"
        _active={{ bgColor: "white" }}
        _expanded={{ bgColor: "white" }}
        _focus={{ bgColor: "white" }}
        _hover={{ bgColor: "white" }}
      >
        <Avatar height="3rem" src={imgSrc} width="3rem">
          {showBadge && <AvatarBadge bg="error-new" boxSize="1.25rem" />}
        </Avatar>
      </MenuButton>
      <MenuList w="16.5rem">
        <Flex color="th.gray3" justify="center" py={1} w="full">
          <Box>{t("navigation.accountHeading")}</Box>
        </Flex>
        <MenuDivider borderColor="th.gray1" />
        <Flex direction="column" gap={2} w="full">
          {!!plan && (
            <>
              <DesktopMenuItem
                onClick={track.settingsLink}
                to={routes.settings}
              >
                <Settings02 h={4} w={4} />
                <Box>{t("navigation.settings")}</Box>
                {showBadge && (
                  <Center>
                    <Circle size={fontSizes.sm} bg="error-new" />
                  </Center>
                )}
              </DesktopMenuItem>
              {isUpgradeable && (
                <DesktopMenuItem
                  onClick={track.upgradeToPremium}
                  state={{ upgrade: true }}
                  to={upgradeRoute}
                >
                  <Stars02 h={4} w={4} />
                  <Box>{t("navigation.upgradeToPremium")}</Box>
                </DesktopMenuItem>
              )}
              <DesktopMenuItem
                onClick={track.referAFriendLink}
                to={routes.referAFriend}
              >
                <HeartHand h={4} w={4} />
                <Box>{t("navigation.referAFriend")}</Box>
              </DesktopMenuItem>
              <DesktopMenuItem
                onClick={track.perksLink}
                to="https://sites.google.com/weconceive.com/memberperks/home"
              >
                <Logo color="th.black" h={4} w={4} />
                <Box>{t("navigation.perks")}</Box>
              </DesktopMenuItem>
            </>
          )}
        </Flex>
        {!!plan && (
          <Flex justify="center" w="full">
            <MenuDivider borderColor="th.gray1" w="80%" />
          </Flex>
        )}
        <MenuItem onClick={handleSignOut}>
          <Flex justify="center" w="full">
            <Box>{t("navigation.signOut")}</Box>
          </Flex>
        </MenuItem>
      </MenuList>
    </Menu>
  );
};
